// app/javascript/controllers/pages_controllers/treppenlift_calculator_controller.js
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "step1", "step2", "step3", "successMessage",
    "step1Indicator", "step2Indicator", "step3Indicator",
    "progressBar", "floorSection", "curveSection",
    "stairType", "floors", "curves",
    "condition", "foldingRail", "powerSwivel", "heavyDuty",
    // "deliveryTime", // Removed if not used
    "name", "email", "phone", "postalCode", "consent",
    "totalPrice", "basePrice", "stairTypeDisplay", "curvesDisplay", "floorsDisplay",
    "conditionDisplay", "foldingRailDisplay", "extrasDisplay", // "deliveryDisplay", // Removed if not used
    "nameError", "emailError", "phoneError", "postalCodeError"
  ]

  // --- Lifecycle Callbacks ---
  connect() {
    this.currentStep = 1
    this.updateProgressBar()
    if (this.hasFloorSectionTarget) {
      this.floorSectionTarget.classList.add("hidden")
    }
    this.updateStairSelection() // Show relevant sections based on default
    this.updatePrice()          // Calculate initial price
  }

  // --- Step Navigation ---
  nextStep() {
    let nextStepTarget = null;

    if (this.currentStep === 1) {
      this.step1Target.classList.add("hidden")
      this.currentStep++
      this.step2Target.classList.remove("hidden")
      nextStepTarget = this.step2Target;
      this.updateProgressBar()
    } else if (this.currentStep === 2) {
      this.step2Target.classList.add("hidden")
      this.currentStep++
      this.step3Target.classList.remove("hidden")
      nextStepTarget = this.step3Target;
      this.updateProgressBar()
    }

    this.scrollToTarget(nextStepTarget);
  }

  previousStep() {
    let previousStepTarget = null;

    if (this.currentStep > 1) {
      this[`step${this.currentStep}Target`].classList.add("hidden")
      this.currentStep--
      this[`step${this.currentStep}Target`].classList.remove("hidden")
      previousStepTarget = this[`step${this.currentStep}Target`];
      this.updateProgressBar()
    }

     this.scrollToTarget(previousStepTarget);
  }

  // --- UI Updates ---
  updateProgressBar() {
    const progress = (this.currentStep / 3) * 100
    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = `${progress}%`
    }

    const indicators = [this.step1IndicatorTarget, this.step2IndicatorTarget, this.step3IndicatorTarget];
    indicators.forEach((indicator, index) => {
      if (indicator) {
        const isActive = index + 1 === this.currentStep;
        const isCompleted = index + 1 < this.currentStep;
        indicator.classList.toggle("font-bold", isActive);
        indicator.classList.toggle("text-ph24_new_blue_90", isActive || isCompleted);
        indicator.classList.toggle("text-ph24_blue", !isActive && !isCompleted);
      }
    });
  }

  updateStairSelection() {
    // Always show floor section once a stair type is selected/defaulted
    if (this.hasFloorSectionTarget) {
      this.floorSectionTarget.classList.remove("hidden")
    }

    const stairType = this.getSelectedValue(this.stairTypeTargets)

    // Show/hide curve section
    if (this.hasCurveSectionTarget) {
        this.curveSectionTarget.classList.toggle("hidden", stairType !== "curved");
        // If showing curves, ensure a default is selected if none is
        if (stairType === "curved" && !this.getSelectedValue(this.curvesTargets)) {
           if (this.curvesTargets.length > 0) {
              this.curvesTargets[0].checked = true; // Default to first curve option
           }
        }
    }

    this.updatePrice()
  }


  // --- Price Calculation ---
  updatePrice() {
    let basePrice = 4000 // Maybe get from a data attribute on element for flexibility
    let totalPrice = basePrice
    let priceDetails = {
      stairType: 0, curves: 0, floors: 0,
      condition: 0, foldingRail: 0, extras: 0
    };

    // Stair Type Cost
    const stairType = this.getSelectedValue(this.stairTypeTargets)
    if (stairType === "curved") priceDetails.stairType = 3000;
    else if (stairType === "spiral") priceDetails.stairType = 3500;

    // Curves Cost (only if curved)
    if (stairType === "curved" && this.hasCurvesTarget) {
      const curves = parseInt(this.getSelectedValue(this.curvesTargets) || '0', 10);
      priceDetails.curves = curves * 1500;
    }

    // Floors Cost
    const floors = parseInt(this.getSelectedValue(this.floorsTargets) || '1', 10);
    if (floors > 1) priceDetails.floors = (floors - 1) * 4000;

    // Calculate subtotal before condition discount
    let subTotal = basePrice + priceDetails.stairType + priceDetails.curves + priceDetails.floors;

    // Condition Discount (applied to subtotal)
    const condition = this.getSelectedValue(this.conditionTargets);
    if (condition === "used") {
      priceDetails.condition = -Math.round(subTotal * 0.25); // Apply 25% discount
    }

    // Extras Cost (added after condition discount)
    if (this.hasFoldingRailTarget && this.foldingRailTarget.checked) priceDetails.foldingRail = 950;
    if (this.hasPowerSwivelTarget && this.powerSwivelTarget.checked) priceDetails.extras += 750;
    if (this.hasHeavyDutyTarget && this.heavyDutyTarget.checked) priceDetails.extras += 900;

    // Calculate Final Total Price
    totalPrice = subTotal + priceDetails.condition + priceDetails.foldingRail + priceDetails.extras;

    // Update Price Summary Display
    this.totalPriceTarget.textContent = `${this.formatPrice(Math.max(0, totalPrice))}€`;
    this.basePriceTarget.textContent = `${this.formatPrice(basePrice)}€`;
    this.updatePriceDisplay(this.stairTypeDisplayTarget, priceDetails.stairType);
    this.updatePriceDisplay(this.curvesDisplayTarget, priceDetails.curves);
    this.updatePriceDisplay(this.floorsDisplayTarget, priceDetails.floors);
    this.updatePriceDisplay(this.conditionDisplayTarget, priceDetails.condition); // Will show negative value
    this.updatePriceDisplay(this.foldingRailDisplayTarget, priceDetails.foldingRail);
    this.updatePriceDisplay(this.extrasDisplayTarget, priceDetails.extras);
  }

  // --- Form Validation & Submission ---
  submitForm() {
    this.resetErrors(); // Clear previous errors first
    let isValid = this.validateForm();

    if (!isValid) {
      // Find the first invalid input and scroll to it
      const firstErrorInput = this.element.querySelector('.border-red-500');
      if (firstErrorInput) {
          this.scrollToTarget(firstErrorInput, 'center');
      }
      return; // Stop submission if invalid
    }

    // If valid, proceed to create lead
    this.createUserAndLead();
  }

  validateForm() {
      let isValid = true;
      // Validate required fields
      if (!this.nameTarget.value.trim()) {
          this.showError('name', 'Name ist erforderlich.');
          isValid = false;
      }
      if (!this.validateEmail(this.emailTarget.value.trim())) {
          this.showError('email', 'Bitte geben Sie eine gültige E-Mail ein.');
          isValid = false;
      }
      if (!this.validatePhoneNumber(this.phoneTarget.value.trim())) {
          this.showError('phone', 'Bitte geben Sie eine gültige Telefonnummer ein.');
          isValid = false;
      }
      if (!this.validateGermanPostalCode(this.postalCodeTarget.value.trim())) {
          this.showError('postalCode', 'PLZ muss 5 Ziffern haben.');
          isValid = false;
      }
      if (!this.consentTarget.checked) {
          // Maybe show error near checkbox? For now, alert is fine.
          alert("Bitte stimmen Sie der Verwendung Ihrer Daten zu.");
          this.consentTarget.classList.add('ring-2', 'ring-red-500'); // Highlight checkbox
          isValid = false;
      }
      return isValid;
  }

  createUserAndLead() {
    const fullName = this.nameTarget.value.trim();
    const nameParts = fullName.split(' ');
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';
    const productId = 6; // Hardcoded for now

    // Gather selected options
    const stairType = this.getSelectedValue(this.stairTypeTargets);
    const condition = this.getSelectedValue(this.conditionTargets);
    const floors = this.getSelectedValue(this.floorsTargets);
    const curves = (stairType === 'curved' && this.hasCurvesTarget) ? this.getSelectedValue(this.curvesTargets) : '0';
    const foldingRail = this.hasFoldingRailTarget && this.foldingRailTarget.checked;
    const powerSwivel = this.hasPowerSwivelTarget && this.powerSwivelTarget.checked;
    const heavyDuty = this.hasHeavyDutyTarget && this.heavyDutyTarget.checked;
    const estimatedPrice = this.totalPriceTarget.textContent;

    const internalNotes = `Treppenlift Kalkulator Anfrage
    ------------------------------
    Konfiguration:
      Treppenart: ${stairType || 'N/A'}
      Stockwerke: ${floors || 'N/A'}
      ${stairType === 'curved' ? `Kurven: ${curves || 'N/A'}` : ''}
      Zustand: ${condition === 'new' ? 'Neu' : (condition === 'used' ? 'Generalüberholt' : 'N/A')}
    Zusatzfunktionen:
      ${foldingRail ? 'Klappschiene: Ja' : 'Klappschiene: Nein'}
      ${powerSwivel ? 'Elektr. Drehfunktion: Ja' : 'Elektr. Drehfunktion: Nein'}
      ${heavyDuty ? 'Verstärkte Version: Ja' : 'Verstärkte Version: Nein'}
    ------------------------------
    Geschätzter Preis: ${estimatedPrice}
    ------------------------------`;

    // Prepare FormData
    const formData = new FormData();
    formData.append('lead[user_attributes][first_name]', firstName);
    formData.append('lead[user_attributes][last_name]', lastName);
    formData.append('lead[user_attributes][email]', this.emailTarget.value.trim());
    formData.append('lead[user_attributes][phone]', this.phoneTarget.value.trim());
    formData.append('lead[user_attributes][shipping_address_attributes][zip_code]', this.postalCodeTarget.value.trim());
    formData.append('lead[product_id]', productId);
    formData.append('lead[status]', 'newly_created');
    formData.append('lead[zip_code]', this.postalCodeTarget.value.trim());
    if (stairType) formData.append('lead[stair_type]', stairType); // Assuming Lead model has these fields
    if (floors) formData.append('lead[number_of_floors]', floors);
    // Add other fields like condition, features if needed on Lead model
    formData.append('lead[internal_notes]', internalNotes.replace(/^ +/gm, '')); // Clean indentation

    const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');

    // Disable submit button during request
    const submitButton = this.element.querySelector('[data-action="treppenlift-calculator#submitForm"]');
    if (submitButton) submitButton.disabled = true;


    fetch('/treppenlift_calculator', { // Make sure route exists
      method: 'POST',
      headers: {
        'X-CSRF-Token': token,
        'Accept': 'application/json'
      },
      body: formData
    })
    .then(response => {
      if (!response.ok) {
        return response.json().then(err => { throw err; }).catch(() => {
          throw new Error(`Serverfehler: ${response.status} ${response.statusText}`);
        });
      }
      return response.json();
    })
    .then(data => {
      this.step3Target.classList.add("hidden");
      this.successMessageTarget.classList.remove("hidden");
      this.scrollToTarget(this.successMessageTarget, 'center');
      // Optionally update progress bar to full?
      // if (this.hasProgressBarTarget) this.progressBarTarget.style.width = "100%";
    })
    .catch(error => {
      console.error('Error submitting form:', error);
      alert(`Es gab ein Problem beim Senden Ihrer Anfrage: ${error.message || 'Bitte versuchen Sie es später erneut.'}`);
      // Re-enable submit button on error
      if (submitButton) submitButton.disabled = false;
    });
  }

  // --- Form Reset ---
  resetForm() {
    // Hide success/steps 2,3, show step 1
    this.successMessageTarget.classList.add("hidden")
    this.step3Target.classList.add("hidden")
    this.step2Target.classList.add("hidden")
    this.step1Target.classList.remove("hidden")

    // Reset state
    this.currentStep = 1
    this.updateProgressBar()

    // Reset all form inputs to defaults programmatically
    this.element.querySelectorAll('input[type="radio"]').forEach(radio => {
        // Check the ones that should be default checked (e.g., first stair type, first floor, 'new' condition)
        radio.checked = radio.hasAttribute('checked');
    });
     this.element.querySelectorAll('input[type="checkbox"]').forEach(checkbox => {
        checkbox.checked = checkbox.hasAttribute('checked'); // Usually false unless explicitly checked in HTML
    });
     this.element.querySelectorAll('input[type="text"], input[type="email"], input[type="tel"]').forEach(input => {
         input.value = '';
     });


    // Reset specific targets if programmatic reset missed something
    if (this.hasConsentTarget) this.consentTarget.checked = false;


    // Update UI based on reset state
    this.updateStairSelection(); // This also calls updatePrice
    this.resetErrors();

    // Scroll to top
    this.scrollToTarget(this.step1Target);
  }


  // --- Validation Helpers ---
  validateGermanPostalCode(postalCode) {
    return /^\d{5}$/.test(postalCode);
  }

  validatePhoneNumber(phoneNumber) {
    const cleaned = phoneNumber.replace(/[\s\-+/()]/g, '');
    // Very basic check for common German patterns (adjust if needed)
    return (/^(0|49)(15|16|17)\d{8,}$/.test(cleaned) || // Mobile
            /^(0|49)[2-9]\d{7,}$/.test(cleaned));      // Landline (simplified)
  }

  validateEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  // --- Error Handling ---
  // *** CORRECTED resetErrors ***
  resetErrors() {
    ['name', 'email', 'phone', 'postalCode'].forEach(field => {
        const capitalizedField = field.charAt(0).toUpperCase() + field.slice(1);
        const hasErrorTarget = this[`has${capitalizedField}ErrorTarget`]; // Check if the boolean property exists and is true
        const hasInputTarget = this[`has${capitalizedField}Target`];     // Check for the input target

        if (hasErrorTarget) {
            const errorEl = this[`${field}ErrorTarget`]; // Access the actual element target
            errorEl.textContent = '';
            errorEl.classList.add('hidden');
        }
        if (hasInputTarget) {
            const inputEl = this[`${field}Target`];      // Access the actual element target
            inputEl.classList.remove('border-red-500', 'ring-1', 'ring-red-500');
        }
    });
     // Also reset consent checkbox visuals if needed
     if (this.hasConsentTarget) {
         this.consentTarget.classList.remove('ring-2', 'ring-red-500');
     }
  }

  showError(field, message) {
      const capitalizedField = field.charAt(0).toUpperCase() + field.slice(1);
      const hasInputTarget = this[`has${capitalizedField}Target`];
      const hasErrorTarget = this[`has${capitalizedField}ErrorTarget`];

      if (hasInputTarget) {
          const inputElement = this[`${field}Target`];
          inputElement.classList.add('border-red-500', 'ring-1', 'ring-red-500');
      }

      if (hasErrorTarget) {
           const errorElement = this[`${field}ErrorTarget`];
           errorElement.textContent = message;
           errorElement.classList.remove('hidden');
      } else {
          // Fallback or log if error target is missing in HTML
          console.warn(`Error target missing for field: ${field}`);
          // Optionally create and insert the error message dynamically here if needed
      }
  }


  // --- Utility Helpers ---
  formatPrice(price) {
    // Basic German formatting
    return price.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  }

  getSelectedValue(targets) {
    const selected = targets.find(target => target.checked);
    return selected ? selected.value : null;
  }

  updatePriceDisplay(target, amount) {
      if (!target) return;
      const shouldShow = amount !== 0;
      target.classList.toggle("hidden", !shouldShow);
      if (shouldShow) {
          const sign = amount > 0 ? "+" : ""; // Negative numbers already have '-'
          target.querySelector("span:last-child").textContent = `${sign}${this.formatPrice(amount)}€`;
      }
  }

  scrollToTarget(target, blockPosition = 'start') {
      if (target) {
          requestAnimationFrame(() => {
              target.scrollIntoView({ behavior: 'smooth', block: blockPosition });
          });
      }
  }
}